"use client";

import { ProductDetailType, ProductVariantAxisType } from "@/app/types/product-site";
import { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useState } from "react";
import { PDPSearchParams } from "../types";
import { pickShowProduct } from "../utils";

interface PdpVariantContextType {
    showProduct: ProductDetailType;
    details: ProductDetailType[];
    variantAxes: ProductVariantAxisType[];
    mounted: boolean;
    selectVariant: (colorCode: string | null, size: string | null) => void;
    selectVariantById: (productId: number) => void;
}

const PdpVariantContext = createContext<PdpVariantContextType | null>(null);

export function usePdpVariant() {
    const ctx = useContext(PdpVariantContext);
    if (!ctx) throw new Error("usePdpVariant must be used within PdpVariantProvider");
    return ctx;
}

export function PdpVariantProvider({ details, variantAxes = [], children }: {
    details: ProductDetailType[];
    variantAxes?: ProductVariantAxisType[];
    children: ReactNode;
}) {
    const [showProduct, setShowProduct] = useState<ProductDetailType>(
        () => pickShowProduct(details, {}) ?? details[0]
    );
    const [mounted, setMounted] = useState(false);

    // Sync with URL params synchronously before browser paints
    useLayoutEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sp: PDPSearchParams = {
            pid: params.get("pid") ?? undefined,
            color_code: params.get("color_code") ?? undefined,
            size: params.get("size") ?? undefined,
        };
        const fromUrl = pickShowProduct(details, sp);
        if (fromUrl) setShowProduct(fromUrl);
        setMounted(true);
    }, []);

    const selectVariant = useCallback((colorCode: string | null, size: string | null) => {
        const sp: PDPSearchParams = {
            color_code: colorCode ?? undefined,
            size: size ?? undefined,
        };
        const next = pickShowProduct(details, sp) ?? details[0];
        setShowProduct(next);

        const urlParams = new URLSearchParams();
        if (colorCode) urlParams.set("color_code", colorCode);
        if (size) urlParams.set("size", size);
        const search = urlParams.toString();
        window.history.replaceState(null, "", window.location.pathname + (search ? "?" + search : ""));
    }, [details]);

    const selectVariantById = useCallback((productId: number) => {
        const next = details.find(product => product.product_id === productId);
        if (!next || next.is_available === false) return;
        setShowProduct(next);
        window.history.replaceState(null, "", `${window.location.pathname}?pid=${productId}`);
    }, [details]);

    return (
        <PdpVariantContext.Provider value={{ showProduct, details, variantAxes, mounted, selectVariant, selectVariantById }}>
            {children}
        </PdpVariantContext.Provider>
    );
}
